import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
  Laptop,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import Swal from 'sweetalert2';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@/components/ThemeProvider';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ImpersonateBanner } from '@/shell/ImpersonateBanner';
import {
  startSessionTimeout,
  stopSessionTimeout,
  pingSessionActivity,
  setSessionTimeoutConfig,
} from '@/utils/sessionTimeout';
import { fetchPlatformSettingsPublic } from '@/store/actions/creators/platformSettingsActions';
import { platformSettingsPublicSelector } from '@/store/selectors/platformSettingsSelector';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import LogoLight from '@/assets/images/logo_smsreflex_light.png';
import LogoDark from '@/assets/images/logo_smsreflex_dark.png';
import IconLight from '@/assets/images/icone_smsreflex_light.png';
import IconDark from '@/assets/images/icone_smsreflex_dark.png';

export type NavItem = {
  /** Route. Optional when the item is a parent (has `children`) and acts purely as a toggle. */
  to?: string;
  label: string;
  icon: LucideIcon;
  /** Sub-items rendered as a collapsible group inside this item. */
  children?: NavItem[];
};

export type NavGroup = {
  label?: string;
  items: NavItem[];
};

const SIDEBAR_COLLAPSED_KEY = 'smsr.sidebar.collapsed';
const SIDEBAR_EXPANDED_KEY = 'smsr.sidebar.expandedParents';

function readBool(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}
function readSet(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

/** True if any descendant leaf's `to` is the current route. */
function hasActiveDescendant(item: NavItem, pathname: string): boolean {
  if (item.to && pathname.startsWith(item.to.split('?')[0])) return true;
  if (item.children) return item.children.some((c) => hasActiveDescendant(c, pathname));
  return false;
}

type AppShellProps = {
  navGroups: NavGroup[];
  user?: {
    name?: string;
    email?: string;
    avatarUrl?: string;
  };
  onLogout?: () => void;
  topbarExtra?: ReactNode;
  userMenuItems?: ReactNode;
  topBanner?: ReactNode;
};

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const Icon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Laptop;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Toggle theme">
          <Icon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setTheme('light')}>
          <Sun className="mr-2 h-4 w-4" /> Light
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme('dark')}>
          <Moon className="mr-2 h-4 w-4" /> Dark
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme('system')}>
          <Laptop className="mr-2 h-4 w-4" /> System
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu({
  user,
  onLogout,
  extraItems,
}: {
  user?: AppShellProps['user'];
  onLogout?: () => void;
  extraItems?: ReactNode;
}) {
  const initials = (user?.name ?? user?.email ?? 'U')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full ring-1 ring-border hover:ring-primary/30"
        >
          <Avatar className="h-8 w-8">
            {user?.avatarUrl && <AvatarImage src={user.avatarUrl} />}
            <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        {(user?.name || user?.email) && (
          <>
            <DropdownMenuLabel>
              <div className="flex flex-col gap-0.5">
                {user?.name && <span className="text-sm">{user.name}</span>}
                {user?.email && (
                  <span className="text-xs font-normal text-muted-foreground">
                    {user.email}
                  </span>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        {extraItems}
        {onLogout && (
          <DropdownMenuItem onClick={onLogout}>
            <LogOut className="mr-2 h-4 w-4" /> Logout
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NavLeaf({
  item,
  collapsed,
  depth,
  onItemClick,
}: {
  item: NavItem;
  collapsed: boolean;
  depth: number;
  onItemClick?: () => void;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to!}
      onClick={onItemClick}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary/10 text-primary'
            : 'text-foreground/75 hover:bg-accent hover:text-foreground',
          collapsed && 'justify-center px-2',
          !collapsed && depth > 0 && 'pl-9',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && !collapsed && (
            <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r-full bg-primary" />
          )}
          <Icon className={cn('h-4 w-4 flex-shrink-0 transition-colors', depth > 0 && !collapsed && 'h-3.5 w-3.5')} />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </>
      )}
    </NavLink>
  );
}

function NavParent({
  item,
  collapsed,
  depth,
  expanded,
  onToggle,
  onCollapsedClick,
  pathname,
  onItemClick,
}: {
  item: NavItem;
  collapsed: boolean;
  depth: number;
  expanded: boolean;
  onToggle: () => void;
  onCollapsedClick: () => void;
  pathname: string;
  onItemClick?: () => void;
}) {
  const Icon = item.icon;
  const childActive = hasActiveDescendant(item, pathname);

  return (
    <>
      <button
        type="button"
        onClick={collapsed ? onCollapsedClick : onToggle}
        title={collapsed ? item.label : undefined}
        aria-expanded={collapsed ? undefined : expanded}
        className={cn(
          'group relative flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          childActive
            ? 'text-primary'
            : 'text-foreground/75 hover:bg-accent hover:text-foreground',
          collapsed && 'justify-center px-2',
        )}
      >
        {childActive && !collapsed && (
          <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r-full bg-primary" />
        )}
        <Icon className="h-4 w-4 flex-shrink-0 transition-colors" />
        {!collapsed && (
          <>
            <span className="truncate">{item.label}</span>
            <ChevronDown
              className={cn(
                'ml-auto h-3.5 w-3.5 flex-shrink-0 text-muted-foreground transition-transform duration-150',
                expanded && 'rotate-180',
              )}
            />
          </>
        )}
      </button>
      {!collapsed && expanded && item.children && (
        <ul className="mt-0.5 space-y-0.5">
          {item.children.map((child) => (
            <li key={child.to ?? child.label}>
              <NavLeaf item={child} collapsed={false} depth={depth + 1} onItemClick={onItemClick} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function SidebarContent({
  navGroups,
  collapsed,
  expandedParents,
  toggleParent,
  onCollapsedParentClick,
  onItemClick,
}: {
  navGroups: NavGroup[];
  collapsed: boolean;
  expandedParents: Set<string>;
  toggleParent: (key: string) => void;
  onCollapsedParentClick: (key: string) => void;
  onItemClick?: () => void;
}) {
  const { pathname } = useLocation();

  return (
    <nav className="scrollbar-none flex-1 overflow-y-auto px-3 py-4">
      {navGroups.map((group, idx) => (
        <div key={idx} className={cn('mb-5', idx === navGroups.length - 1 && 'mb-0')}>
          {group.label && !collapsed && (
            <h3 className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {group.label}
            </h3>
          )}
          <ul className="space-y-0.5">
            {group.items.map((item) => {
              const isParent = !!item.children?.length;
              if (isParent) {
                const childActive = hasActiveDescendant(item, pathname);
                const expanded = expandedParents.has(item.label) || childActive;
                return (
                  <li key={item.label}>
                    <NavParent
                      item={item}
                      collapsed={collapsed}
                      depth={0}
                      expanded={expanded}
                      onToggle={() => toggleParent(item.label)}
                      onCollapsedClick={() => onCollapsedParentClick(item.label)}
                      pathname={pathname}
                      onItemClick={onItemClick}
                    />
                  </li>
                );
              }
              return (
                <li key={item.to ?? item.label}>
                  <NavLeaf item={item} collapsed={collapsed} depth={0} onItemClick={onItemClick} />
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function DesktopSidebar({
  navGroups,
  collapsed,
  onToggle,
  expandedParents,
  toggleParent,
  onCollapsedParentClick,
}: {
  navGroups: NavGroup[];
  collapsed: boolean;
  onToggle: () => void;
  expandedParents: Set<string>;
  toggleParent: (key: string) => void;
  onCollapsedParentClick: (key: string) => void;
}) {
  return (
    <aside
      className={cn(
        'hidden md:flex h-screen flex-col border-r bg-card transition-[width] duration-200 ease-out',
        collapsed ? 'w-[68px]' : 'w-64',
      )}
    >
      <div
        className={cn(
          'flex h-16 items-center border-b',
          collapsed ? 'flex-col gap-1 px-2 py-2' : 'flex-row justify-between px-4',
        )}
      >
        {collapsed ? (
          <>
            <img src={IconLight} alt="SMSReflex" className="h-7 w-auto dark:hidden" />
            <img src={IconDark} alt="SMSReflex" className="hidden h-7 w-auto dark:block" />
          </>
        ) : (
          <>
            <img src={LogoLight} alt="SMSReflex" className="h-7 w-auto dark:hidden" />
            <img src={LogoDark} alt="SMSReflex" className="hidden h-7 w-auto dark:block" />
          </>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={cn(collapsed ? 'h-6 w-6' : 'h-8 w-8')}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>
      <SidebarContent
        navGroups={navGroups}
        collapsed={collapsed}
        expandedParents={expandedParents}
        toggleParent={toggleParent}
        onCollapsedParentClick={onCollapsedParentClick}
      />
    </aside>
  );
}

function MobileSidebar({
  navGroups,
  open,
  onClose,
  expandedParents,
  toggleParent,
}: {
  navGroups: NavGroup[];
  open: boolean;
  onClose: () => void;
  expandedParents: Set<string>;
  toggleParent: (key: string) => void;
}) {
  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm transition-opacity md:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r bg-card transition-transform duration-200 ease-out md:hidden',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center justify-between border-b px-4">
          <img src={LogoLight} alt="SMSReflex" className="h-7 w-auto dark:hidden" />
          <img src={LogoDark} alt="SMSReflex" className="hidden h-7 w-auto dark:block" />
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close sidebar"
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <SidebarContent
          navGroups={navGroups}
          collapsed={false}
          expandedParents={expandedParents}
          toggleParent={toggleParent}
          onCollapsedParentClick={() => undefined}
          onItemClick={onClose}
        />
      </aside>
    </>
  );
}

export function AppShell({
  navGroups,
  user,
  onLogout,
  topbarExtra,
  userMenuItems,
  topBanner,
  children,
}: AppShellProps & { children?: ReactNode }) {
  const [collapsed, setCollapsed] = useState<boolean>(() => readBool(SIDEBAR_COLLAPSED_KEY));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedParents, setExpandedParents] = useState<Set<string>>(() => readSet(SIDEBAR_EXPANDED_KEY));
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const platformSettings = useSelector(platformSettingsPublicSelector) as any;

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {}
  }, [collapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_EXPANDED_KEY, JSON.stringify(Array.from(expandedParents)));
    } catch {}
  }, [expandedParents]);

  const toggleParent = useCallback((key: string) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const onCollapsedParentClick = useCallback((key: string) => {
    setCollapsed(false);
    setExpandedParents((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  // Auto-expand the parent of the active route (so a refresh keeps the
  // submenu open without burying the user in their breadcrumbs).
  const allParents = useMemo(() => {
    const out: NavItem[] = [];
    const walk = (items: NavItem[]) => {
      for (const it of items) {
        if (it.children?.length) {
          out.push(it);
          walk(it.children);
        }
      }
    };
    navGroups.forEach((g) => walk(g.items));
    return out;
  }, [navGroups]);

  useEffect(() => {
    setExpandedParents((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const p of allParents) {
        if (hasActiveDescendant(p, location.pathname) && !next.has(p.label)) {
          next.add(p.label);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [allParents, location.pathname]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
    pingSessionActivity();
  }, [location.pathname]);

  // Pull the timeout config once after authentication and start the timer.
  useEffect(() => {
    if (!platformSettings) {
      // @ts-expect-error legacy redux-thunk
      dispatch(fetchPlatformSettingsPublic());
    }
  }, []);

  useEffect(() => {
    if (platformSettings) {
      setSessionTimeoutConfig({
        inactivityTimeoutMinutes: platformSettings.inactivityTimeoutMinutes,
        inactivityWarningMinutes: platformSettings.inactivityWarningMinutes,
      });
    }
  }, [platformSettings?.inactivityTimeoutMinutes, platformSettings?.inactivityWarningMinutes]);

  useEffect(() => {
    let alertOpen = false;

    const onWarn = async () => {
      if (alertOpen) return;
      alertOpen = true;
      const result = await Swal.fire({
        icon: 'warning',
        title: t('SESSION.WARNING_TITLE') ?? 'Session about to expire',
        text:
          t('SESSION.WARNING_TEXT') ??
          'Your session will end soon due to inactivity. Continue?',
        showCancelButton: true,
        confirmButtonText: t('SESSION.CONTINUE') ?? 'Continue',
        cancelButtonText: t('SESSION.LOGOUT_NOW') ?? 'Sign out',
        confirmButtonColor: 'hsl(var(--primary))',
        cancelButtonColor: '#64748b',
        reverseButtons: true,
        focusConfirm: true,
      });
      alertOpen = false;
      if (result.isConfirmed) {
        pingSessionActivity();
      } else {
        window.location.href = '/logout';
      }
    };

    const onTimeout = () => {
      Swal.close();
      window.location.href = '/logout';
    };

    startSessionTimeout({ onWarn, onTimeout });
    return () => stopSessionTimeout();
  }, [t]);

  // Any user input also resets the timer (mouse / keyboard / touch).
  useEffect(() => {
    const handler = () => pingSessionActivity();
    window.addEventListener('mousemove', handler, { passive: true });
    window.addEventListener('keydown', handler);
    window.addEventListener('touchstart', handler, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handler);
      window.removeEventListener('keydown', handler);
      window.removeEventListener('touchstart', handler);
    };
  }, []);

  return (
    <div className="flex h-screen bg-muted/30">
      <DesktopSidebar
        navGroups={navGroups}
        collapsed={collapsed}
        onToggle={() => setCollapsed((v) => !v)}
        expandedParents={expandedParents}
        toggleParent={toggleParent}
        onCollapsedParentClick={onCollapsedParentClick}
      />
      <MobileSidebar
        navGroups={navGroups}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        expandedParents={expandedParents}
        toggleParent={toggleParent}
      />
      <div className="flex flex-1 flex-col overflow-hidden">
        <ImpersonateBanner />
        <header className="sticky top-0 z-30 flex h-16 items-center gap-2 border-b bg-card/80 px-4 backdrop-blur-md md:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open sidebar"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1" />
          {topbarExtra}
          <ThemeToggle />
          <UserMenu user={user} onLogout={onLogout} extraItems={userMenuItems} />
        </header>
        {/* `relative` : ancre dans le scroll les éléments position:absolute cachés
            (select natif Radix, checkbox/sr-only) ; sinon ils s'accrochent à <body>
            et la page défile dans le vide sous le shell. */}
        <main className="relative flex-1 overflow-y-auto">
          {topBanner}
          <div className="mx-auto w-full max-w-7xl p-4 md:p-6 lg:p-8">
            {children ?? <Outlet />}
          </div>
        </main>
      </div>
    </div>
  );
}
